import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { FiMessageSquare, FiStar, FiCode, FiShield, FiCheck, FiX } from 'react-icons/fi';
import logo from '../assets/images/logo.svg';
import { trackSignupFree } from '../utils/analytics.js';
import './ScrollSignupPopup.css';

function ScrollSignupPopup({ slug, signupUrl = 'https://clientalio.com/signup' }) {
  const storageKey = `blog_signup_dismissed_v2_${slug || 'default'}`;
  const DISMISS_TTL = 12 * 60 * 60 * 1000; // 12 hours
  const [visible, setVisible] = useState(false);
  const modalRef = useRef(null);
  const lastFocusedRef = useRef(null);

  const hide = useCallback(() => {
    try { localStorage.setItem(storageKey, JSON.stringify({ dismissedAt: Date.now() })); } catch (e) { /* ignore */ }
    setVisible(false);
    // restore focus
    try { if (lastFocusedRef.current) lastFocusedRef.current.focus(); } catch (e) { }
  }, [storageKey]);

  // Show once the reader reaches 50% of the page's scrollable distance.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.dismissedAt) {
            const age = Date.now() - parsed.dismissedAt;
            if (age < DISMISS_TTL) {
              return undefined;
            }
            // expired
            localStorage.removeItem(storageKey);
          } else {
            // fallback: if value present but not JSON, treat as dismissed
            return undefined;
          }
        } catch (e) {
          return undefined;
        }
      }
    } catch (e) {
      // ignore storage access errors
    }

    let animationFrameId = null;
    let triggered = false;

    function checkProgress() {
      animationFrameId = null;

      if (triggered) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollableHeight > 0 ? scrollTop / scrollableHeight : 0;

      if (progress >= 0.5) {
        triggered = true;
        lastFocusedRef.current = document.activeElement;
        setVisible(true);
        window.removeEventListener('scroll', requestProgressCheck);
        window.removeEventListener('resize', requestProgressCheck);
      }
    }

    function requestProgressCheck() {
      if (animationFrameId === null) {
        animationFrameId = window.requestAnimationFrame(checkProgress);
      }
    }

    window.addEventListener('scroll', requestProgressCheck, { passive: true });
    window.addEventListener('resize', requestProgressCheck);
    requestProgressCheck();

    return () => {
      window.removeEventListener('scroll', requestProgressCheck);
      window.removeEventListener('resize', requestProgressCheck);

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [storageKey]);

  // accessibility: trap focus & handle ESC
  useEffect(() => {
    if (!visible) return undefined;

    const focusableSelector = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const modal = modalRef.current;
    const focusable = modal ? Array.from(modal.querySelectorAll(focusableSelector)) : [];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        hide();
        return;
      }

      if (e.key === 'Tab') {
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    // focus the modal container for screen readers
    try { (first || modal).focus(); } catch (e) { }

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible, hide]);

  if (!visible) return null;

  return (
    <div className="scroll-signup-popup-overlay" role="dialog" aria-modal="true" aria-labelledby="scroll-signup-title">
      <div className="scroll-signup-backdrop" onClick={hide} />

      <div className="scroll-signup-modal" ref={modalRef} role="document">
        <button className="scroll-signup-close" type="button" onClick={hide} aria-label="Close signup popup">
          <FiX aria-hidden="true" />
        </button>
        <div className="scroll-signup-content">
            <div className="scroll-signup-header">
              <div className="logo-wrap">
                <img src={logo} alt="Clientalio" className="clientalio-logo" />
              </div>
              <div className="header-badge"><span className="header-stars">★★★★★</span> <span className="beta">BETA</span></div>
            </div>

            <div className="scroll-signup-eyebrow">REAL STORIES. REAL GROWTH.</div>

            <h2 id="scroll-signup-title">Turn Customer Love Into <span className="highlight">Business Growth</span></h2>
            <p className="scroll-signup-lead">Collect, manage, and showcase testimonials with beautiful Wall of Love pages and embeddable widgets that help you convert more buyers.</p>

            <div className="scroll-signup-features">
              <div className="feature">
                <div className="feature-icon" aria-hidden><FiMessageSquare /></div>
                <div>
                  <div className="feature-title">Collect Testimonials</div>
                  <div className="feature-sub">Get authentic customer feedback in minutes.</div>
                </div>
              </div>
              <div className="feature">
                <div className="feature-icon" aria-hidden><FiStar /></div>
                <div>
                  <div className="feature-title">Wall of Love</div>
                  <div className="feature-sub">Turn testimonials into beautiful pages.</div>
                </div>
              </div>
              <div className="feature">
                <div className="feature-icon" aria-hidden><FiCode /></div>
                <div>
                  <div className="feature-title">Embed Anywhere</div>
                  <div className="feature-sub">Add customer proof to your website with no code.</div>
                </div>
              </div>
              <div className="feature">
                <div className="feature-icon" aria-hidden><FiShield /></div>
                <div>
                  <div className="feature-title">Build Trust</div>
                  <div className="feature-sub">Turn customer stories into conversion assets.</div>
                </div>
              </div>
            </div>

            <div className="scroll-signup-cta-row">
              <div className="scroll-signup-cta-actions">
                <a className="scroll-signup-cta" href={signupUrl} onClick={() => trackSignupFree('scroll_popup')}>Start FREE — Create Your Wall of Love <FaArrowRight aria-hidden="true" /></a>
                <button className="scroll-signup-secondary" onClick={hide}>Maybe later</button>
              </div>

              <div className="scroll-signup-trust">
                <span><FiCheck className="check" aria-hidden="true"/> No credit card required</span>
                <span><FiCheck className="check" aria-hidden="true"/> Setup in under 2 minutes</span>
                <span><FiCheck className="check" aria-hidden="true"/> Free to get started</span>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
}

export default ScrollSignupPopup;
